import { useCallback } from 'react';

import {
  MailSignaturesListDocument,
  MailSignaturesListQuery,
  usePullGmailSignatureMutation,
  useStoreMailSignatureMutation,
  useMailSignaturesListQuery,
} from 'core/graphql';

import { IRichMailError, richMailError } from './richMailModel';

export type IMailSignature = MailSignaturesListQuery['myMailSignatures'][number];

const EMPTY: IMailSignature[] = [];

export interface IMailSignatureResult {
  ok: boolean;
  signature: IMailSignature | null;
  error: IRichMailError | null;
}

/**
 * ТЗ 0097 — свои подписи писем. Список нужен и формам (подпись к письму), и
 * разделу настроек; `cache-first` — подписи меняются редко, а форм письма на
 * экране бывает несколько.
 */
export const useMailSignatures = ({
  skip = false,
}: { skip?: boolean } = {}) => {
  const { data, loading, error } = useMailSignaturesListQuery({
    skip,
    fetchPolicy: 'cache-first',
  });
  return {
    signatures: data?.myMailSignatures ?? EMPTY,
    loading,
    failed: Boolean(error),
  };
};

/**
 * Сохранить / подтянуть из Gmail. Список перечитывается по имени документа:
 * удаление (пустой HTML) возвращает `null`, его в кэш не положить.
 */
export const useMailSignatureActions = () => {
  const [storeMut, { loading: saving }] = useStoreMailSignatureMutation();
  const [pullMut, { loading: importing }] = usePullGmailSignatureMutation();

  const save = useCallback(
    async (
      mailboxEmail: string | null,
      html: string,
    ): Promise<IMailSignatureResult> => {
      try {
        const { data } = await storeMut({
          variables: { mailboxEmail, html },
          refetchQueries: [{ query: MailSignaturesListDocument }],
          awaitRefetchQueries: true,
        });
        return {
          ok: true,
          signature: data?.saveMailSignature ?? null,
          error: null,
        };
      } catch (caught) {
        return { ok: false, signature: null, error: richMailError(caught) };
      }
    },
    [storeMut],
  );

  const importFromGmail = useCallback(
    async (mailboxEmail: string): Promise<IMailSignatureResult> => {
      try {
        const { data } = await pullMut({
          variables: { mailboxEmail },
          refetchQueries: [{ query: MailSignaturesListDocument }],
          awaitRefetchQueries: true,
        });
        return {
          ok: true,
          signature: data?.importGmailSignature ?? null,
          error: null,
        };
      } catch (caught) {
        return { ok: false, signature: null, error: richMailError(caught) };
      }
    },
    [pullMut],
  );

  return { save, importFromGmail, saving, importing };
};
